import {
  type ElementType,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import { useNavigate } from "react-router-dom";
import {
  selectedBrandSelector,
  pageSetupSelector,
  brandIsLoadingSelector,
} from "@/shared/store/brandSlice";
import { getPageSetupThunk } from "@/shared/store/brandSlice";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import RpMUIDialogBase from "@/components/modals/RpMUIModal";
import SelectBrand from "@/shared/components/SelectBrand";
import { isPasswordChangeRequiredSelector } from "@/features/auth/store/authSlice";
import ForcedPasswordChangeFormBase from "@/features/auth/components/ForcedPasswordChangeForm";
import { logoutAction } from "@/features/auth/store/logoutAction";

import PageHeaderBase from "@/components/layouts/pageHeaders/PageHeader";
import PageLayout from "@/components/layouts/page/PageLayout";
import { getDashboardPath } from "@/helpers/utils";

type HomeRecord = Record<string, any>;
type GlobalContextValue = {
  loggedInUser?: HomeRecord;
};
type Loader = (_show: boolean) => void;
type Dispatch = (_action: any) => any;

const PageHeader = PageHeaderBase as ElementType;
const RpMUIDialog = RpMUIDialogBase as ElementType;
const ForcedPasswordChangeForm = ForcedPasswordChangeFormBase as ElementType;

const isTermsAccepted = (user: HomeRecord = {}) => {
  const isTermsAcceptedValue = user?.is_terms_accepted;

  if (isTermsAcceptedValue === null) return false;

  return (
    isTermsAcceptedValue === true ||
    isTermsAcceptedValue === 1 ||
    isTermsAcceptedValue === "1" ||
    isTermsAcceptedValue === "true"
  );
};

const Home = () => {
  const dispatch = useDispatch() as Dispatch;
  const navigate = useNavigate();
  const loader = useContext(LoaderContext) as Loader;
  const { loggedInUser } = useContext(GlobalContext) as GlobalContextValue;
  const selectedBrand = useSelector(selectedBrandSelector);
  const pageSetup = useSelector(pageSetupSelector);
  const isLoading = useSelector(brandIsLoadingSelector);
  const isPasswordChangeRequired = useSelector(
    isPasswordChangeRequiredSelector
  );
  const getPageSetup = useCallback(
    (id: string | number) => dispatch(getPageSetupThunk(id)),
    [dispatch]
  );

  const [openSelectBrand, setOpenSelectBrand] = useState(false);
  const isTermsAcceptancePending =
    loggedInUser?.id &&
    !loggedInUser?.is_master_pass &&
    !isTermsAccepted(loggedInUser);

  useEffect(() => {
    loader(isLoading);
  }, [isLoading, loader]);

  useEffect(() => {
    if (isPasswordChangeRequired || isTermsAcceptancePending) {
      setOpenSelectBrand(false);
      return;
    }

    if (!selectedBrand?.id) {
      setOpenSelectBrand(true);
    } else {
      setOpenSelectBrand(false);
      getPageSetup(selectedBrand.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id, isPasswordChangeRequired, isTermsAcceptancePending]);

  useEffect(() => {
    if (pageSetup && selectedBrand?.id) {
      setOpenSelectBrand(false);
    }
  }, [pageSetup, selectedBrand?.id]);

  useEffect(() => {
    if (
      loggedInUser?.id &&
      selectedBrand?.id &&
      !isPasswordChangeRequired &&
      !isTermsAcceptancePending
    ) {
      const dashboardPath = getDashboardPath();
      navigate(`${dashboardPath}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loggedInUser?.id,
    selectedBrand?.id,
    isPasswordChangeRequired,
    isTermsAcceptancePending,
  ]);

  const handleForcedPasswordUpdate = (payload?: { success?: boolean }) => {
    const isUpdated = payload?.success ?? true;
    if (isUpdated) {
      dispatch(logoutAction());
      toastr.success(
        "Success",
        "Password updated successfully. Please login with your new password."
      );
    }
  };

  return (
    <>
      <PageLayout className="participant-dashboard-container mx-0 mx-sm-2">
        <PageHeader showName showLastUpdate={false} showButton={true} />
      </PageLayout>
      <RpMUIDialog
        open={openSelectBrand}
        onClose={() => setOpenSelectBrand(false)}
        heading="Select Brand"
        scroll="body"
        persists
      >
        <SelectBrand />
      </RpMUIDialog>
      <RpMUIDialog
        open={isPasswordChangeRequired}
        onClose={() => null}
        maxWidth="sm"
        fullWidth
        persists
      >
        <div className="px-2 py-1 py-sm-2">
          <ForcedPasswordChangeForm
            onPasswordUpdated={handleForcedPasswordUpdate}
          />
        </div>
      </RpMUIDialog>
    </>
  );
};

export default Home;
